/**
 * Free-text "search by brand" box shown next to the Sort by control on
 * collection pages and the search results page.
 *
 * The customer types any brand name (no fixed list). Matching is done
 * against the product TITLE across EVERY product in the collection (or every
 * product the current search matched), results sorted newest first, and the
 * grid updates in place with no full page reload.
 *
 * Mobile performance (Oct 2026 rewrite):
 *  - Collections are read through a lightweight JSON view
 *    (templates/collection.brand-search.liquid) instead of the heavy
 *    /products.json (which returned every variant and every full-size image
 *    for thousands of products and froze phones).
 *  - Images are requested at a small size (400px) instead of the original
 *    multi-megapixel photos.
 *  - Pages are fetched in parallel (a few at a time) instead of one by one.
 *  - Results are rendered in batches of 24 with a "Show more" button
 *    instead of 200 cards at once.
 */
(function () {
  'use strict';

  var RENDER_BATCH = 24;
  var FETCH_CONCURRENCY = 4;
  var MAX_PAGES = 200;
  var IMAGE_WIDTH = 400;
  var DATE_SORT_VALUE = 'created-descending';

  var scopeCache = {};

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  function getStrings() {
    var defaults = {
      loading: 'Searching…',
      noResults: 'No products found for "{{ terms }}".',
      resultsCount: 'Showing {{ count }} matching products',
      dateSortedStatus: 'Showing {{ count }} products, newest first',
      soldOut: 'Sold out',
      showMore: 'Show more',
    };
    var provided = window.brandSearchStrings || {};
    return {
      loading: provided.loading || defaults.loading,
      noResults: defaults.noResults,
      resultsCount: defaults.resultsCount,
      dateSortedStatus: defaults.dateSortedStatus,
      soldOut: provided.soldOut || defaults.soldOut,
      showMore: defaults.showMore,
    };
  }

  function formatString(template, replacements) {
    return template.replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(replacements, key) ? replacements[key] : match;
    });
  }

  function resizeImage(url, width) {
    if (!url) return null;
    if (url.indexOf('//') === 0) url = 'https:' + url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + width);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function scopeKeyFor(container) {
    var scope = container.dataset.brandSearchScope;
    if (scope === 'collection') {
      return 'collection:' + container.dataset.brandSearchCollection;
    }
    return 'search:' + (container.dataset.brandSearchTerms || '');
  }

  function fetchAllProducts(container) {
    var key = scopeKeyFor(container);
    if (scopeCache[key]) return scopeCache[key];

    var scope = container.dataset.brandSearchScope;
    var promise;

    if (scope === 'collection') {
      var handle = container.dataset.brandSearchCollection;
      promise = fetchAllPages(function (page) {
        return '/collections/' + encodeURIComponent(handle) + '?view=brand-search&card_sizes=1&page=' + page;
      }, parseLightPage).then(function (products) {
        if (products && products.length) return products;
        // Fallback (e.g. the light view template is missing): the old
        // products.json endpoint, with small images.
        return fetchAllPages(function (page) {
          return '/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + page;
        }, parseCollectionPage);
      });
    } else {
      var terms = container.dataset.brandSearchTerms || '';
      promise = fetchAllPages(function (page) {
        return (
          '/search?view=brand-search&card_sizes=1&type=product&options%5Bprefix%5D=last&q=' +
          encodeURIComponent(terms) +
          '&page=' +
          page
        );
      }, parseLightPage);
    }

    promise = promise.catch(function () {
      delete scopeCache[key];
      return [];
    });
    scopeCache[key] = promise;
    return promise;
  }

  function fetchPage(url, parsePage) {
    return fetch(url, { headers: { accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) return { products: [], hasNextPage: false, pages: 0 };
        return response.text().then(function (text) {
          try {
            return parsePage(JSON.parse(text));
          } catch (e) {
            return { products: [], hasNextPage: false, pages: 0 };
          }
        });
      })
      .catch(function () {
        return { products: [], hasNextPage: false, pages: 0 };
      });
  }

  // Page 1 tells us how many pages exist; the rest are fetched in parallel,
  // a few at a time, and stitched back together in page order.
  function fetchAllPages(urlForPage, parsePage) {
    return fetchPage(urlForPage(1), parsePage).then(function (first) {
      var results = [first.products];
      var totalPages = first.pages || 0;

      if (!first.products.length) return [];

      if (!totalPages) {
        // Unknown page count (products.json fallback): walk sequentially.
        var all = first.products.slice();
        var walk = function (page, hasNext) {
          if (!hasNext || page > MAX_PAGES) return Promise.resolve(all);
          return fetchPage(urlForPage(page), parsePage).then(function (parsed) {
            all = all.concat(parsed.products);
            return walk(page + 1, parsed.hasNextPage && parsed.products.length > 0);
          });
        };
        return walk(2, first.hasNextPage);
      }

      totalPages = Math.min(totalPages, MAX_PAGES);
      var nextPage = 2;

      function worker() {
        if (nextPage > totalPages) return Promise.resolve();
        var page = nextPage++;
        return fetchPage(urlForPage(page), parsePage).then(function (parsed) {
          results[page - 1] = parsed.products;
          return worker();
        });
      }

      var workers = [];
      for (var i = 0; i < FETCH_CONCURRENCY; i++) workers.push(worker());
      return Promise.all(workers).then(function () {
        var merged = [];
        for (var p = 0; p < results.length; p++) {
          if (results[p]) merged = merged.concat(results[p]);
        }
        return merged;
      });
    });
  }

  function fallbackSizeChips(options) {
    var option = (options || []).find(function (entry) { return String(entry.name || '').toLowerCase() === 'size'; });
    var values = option && option.values || [];
    var named = ['nano', 'mini', 'small', 'medium', 'large'];
    if (values.length < 2 || !values.every(function (value) { return /cm|mm/i.test(value) || named.indexOf(String(value).toLowerCase()) !== -1; })) return '';
    values = values.slice();
    if (values.every(function (value) { return named.indexOf(String(value).toLowerCase()) !== -1; })) {
      values.sort(function (a, b) { return named.indexOf(a.toLowerCase()) - named.indexOf(b.toLowerCase()); });
    }
    return '<div class="card-size-chips" style="display:flex;flex-wrap:wrap;gap:5px;margin:8px 0 2px;">' + values.map(function (value) {
      var label = { small: 'S', medium: 'M', large: 'L' }[String(value).toLowerCase()] || value;
      return '<span class="card-size-chip" title="' + escapeHtml(value) + '" style="display:inline-flex;align-items:center;justify-content:center;min-width:24px;height:24px;padding:0 6px;box-sizing:border-box;border-radius:999px;background:#b5935b;color:#ffffff;font-weight:700;font-size:11px;letter-spacing:.04em;line-height:1;text-transform:uppercase;white-space:nowrap;">' + escapeHtml(label) + '</span>';
    }).join('') + '</div>';
  }

  function parseCollectionPage(json) {
    var products = (json.products || []).map(function (product) {
      var variant = (product.variants && product.variants[0]) || {};
      var image = (product.images && product.images[0]) || null;
      return {
        title: product.title,
        url: '/products/' + product.handle,
        available:
          !!product.variants &&
          product.variants.some(function (v) {
            return v.available;
          }),
        price: variant.price != null ? parseFloat(variant.price) : null,
        compareAtPrice: variant.compare_at_price != null ? parseFloat(variant.compare_at_price) : null,
        image: image ? resizeImage(image.src, IMAGE_WIDTH) : null,
        imageAlt: (image && image.alt) || product.title,
        sizeChips: fallbackSizeChips(product.options),
        createdAt: product.created_at ? Date.parse(product.created_at) : 0,
      };
    });
    return {
      products: products,
      hasNextPage: (json.products || []).length >= 250,
      pages: 0,
    };
  }

  // Shape emitted by templates/search.brand-search.liquid and
  // templates/collection.brand-search.liquid.
  function parseLightPage(json) {
    var products = (json.products || []).map(function (product) {
      return {
        title: product.title,
        url: product.url,
        available: !!product.available,
        price: product.price != null ? parseFloat(product.price) : null,
        compareAtPrice: product.compare_at_price != null ? parseFloat(product.compare_at_price) : null,
        image: product.image ? resizeImage(product.image, IMAGE_WIDTH) : null,
        imageAlt: product.image_alt || product.title,
        sizeChips: typeof product.size_chips === 'string' ? product.size_chips : '',
        // created_at is emitted as Unix seconds via Liquid's date: '%s'.
        createdAt: product.created_at ? parseInt(product.created_at, 10) * 1000 : 0,
      };
    });
    return {
      products: products,
      hasNextPage: !!json.has_next_page,
      pages: parseInt(json.pages, 10) || 0,
    };
  }

  function formatMoney(amount) {
    try {
      var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AED';
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function renderCard(product) {
    var li = document.createElement('li');
    li.className = 'grid__item';
    li.setAttribute('data-brand-search-card', 'true');

    var badge = '';
    if (product.available === false) {
      badge =
        '<div class="card__badge badge-position"><span class="badge badge--bottom-left">' +
        escapeHtml(getStrings().soldOut) +
        '</span></div>';
    }

    var priceHtml = '';
    if (product.price != null) {
      if (product.compareAtPrice && product.compareAtPrice > product.price) {
        priceHtml =
          '<div class="price price--on-sale"><div class="price__container">' +
          '<div class="price__sale"><s class="price-item price-item--regular">' +
          formatMoney(product.compareAtPrice) +
          '</s> <span class="price-item price-item--sale price-item--last">' +
          formatMoney(product.price) +
          '</span></div></div></div>';
      } else {
        priceHtml =
          '<div class="price"><div class="price__container"><div class="price__regular">' +
          '<span class="price-item price-item--regular">' +
          formatMoney(product.price) +
          '</span></div></div></div>';
      }
    }

    // card__content must be a direct child of .card (matches Dawn markup).
    li.innerHTML =
      '<div class="card-wrapper product-card-wrapper underline-links-hover">' +
      '<div class="card card--standard card--media" style="--ratio-percent: 125%;">' +
      '<div class="card__inner ratio" style="--ratio-percent: 125%;">' +
      '<div class="card__media"><div class="media media--transparent">' +
      (product.image
        ? '<img src="' +
          escapeHtml(product.image) +
          '" alt="' +
          escapeHtml(product.imageAlt || '') +
          '" loading="lazy" decoding="async" width="400" height="500" class="motion-reduce">'
        : '') +
      '</div></div>' +
      '</div>' +
      '<div class="card__content"><div class="card__information">' +
      '<h3 class="card__heading h5"><a href="' +
      escapeHtml(product.url) +
      '" class="full-unstyled-link">' +
      escapeHtml(product.title) +
      '</a></h3>' +
      '<div class="card-information">' +
      priceHtml +
      (product.sizeChips || '') +
      '</div>' +
      '</div>' +
      badge +
      '</div>' +
      '</div>' +
      '</div>';

    return li;
  }

  function getGridContainer() {
    return document.getElementById('ProductGridContainer');
  }

  function getProductGridList(gridContainer) {
    return gridContainer.querySelector('ul.product-grid');
  }

  function getInfiniteScrollEl() {
    return document.querySelector('infinite-scroll');
  }

  // Removes duplicate grids spliced in by Dawn's infinite-scroll, plus
  // pagination/loading overlays and our own previous "Show more" button.
  function cleanGridContainer(gridContainer) {
    var lists = gridContainer.querySelectorAll('ul.product-grid');
    var list = lists.length ? lists[0] : null;
    for (var i = 1; i < lists.length; i++) {
      lists[i].remove();
    }
    Array.prototype.forEach.call(
      gridContainer.querySelectorAll('.loading-overlay, .pagination, .brand-search-more-wrap'),
      function (el) {
        el.remove();
      }
    );
    return list;
  }

  function detachInfiniteScroll() {
    var infiniteScrollEl = getInfiniteScrollEl();
    if (infiniteScrollEl && infiniteScrollEl.parentNode) {
      infiniteScrollEl.parentNode.removeChild(infiniteScrollEl);
    }
  }

  // Keep any late-arriving native infinite-scroll items out of our list.
  var gridGuard = null;

  function guardGridList(list) {
    if (gridGuard && gridGuard.target === list) return;
    if (gridGuard) gridGuard.observer.disconnect();
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType === 1 && !node.hasAttribute('data-brand-search-card')) {
            node.remove();
          }
        });
      });
    });
    observer.observe(list, { childList: true });
    gridGuard = { observer: observer, target: list };
  }

  function stopGuardingGrid() {
    if (gridGuard) {
      gridGuard.observer.disconnect();
      gridGuard = null;
    }
  }

  function ensureStatusEl(gridContainer) {
    var status = gridContainer.querySelector('.brand-search-status');
    if (!status) {
      status = document.createElement('p');
      status.className = 'brand-search-status';
      status.setAttribute('role', 'status');
      status.hidden = true;
      gridContainer.insertBefore(status, gridContainer.firstChild);
    }
    return status;
  }

  var originalGridSnapshot = null;
  var infiniteScrollOriginal = null;

  function captureSnapshotIfNeeded() {
    if (originalGridSnapshot !== null) return;
    var gridContainer = getGridContainer();
    if (gridContainer) {
      originalGridSnapshot = gridContainer.innerHTML;
    }
    var infiniteScrollEl = getInfiniteScrollEl();
    if (infiniteScrollEl && infiniteScrollOriginal === null) {
      infiniteScrollOriginal = {
        el: infiniteScrollEl,
        parent: infiniteScrollEl.parentNode,
        nextSibling: infiniteScrollEl.nextSibling,
      };
    }
  }

  function restoreSnapshot() {
    stopGuardingGrid();
    var gridContainer = getGridContainer();
    if (gridContainer && originalGridSnapshot !== null) {
      gridContainer.innerHTML = originalGridSnapshot;
    }
    if (infiniteScrollOriginal && !document.contains(infiniteScrollOriginal.el) && infiniteScrollOriginal.parent) {
      if (
        infiniteScrollOriginal.nextSibling &&
        infiniteScrollOriginal.nextSibling.parentNode === infiniteScrollOriginal.parent
      ) {
        infiniteScrollOriginal.parent.insertBefore(infiniteScrollOriginal.el, infiniteScrollOriginal.nextSibling);
      } else {
        infiniteScrollOriginal.parent.appendChild(infiniteScrollOriginal.el);
      }
    }
  }

  function prepareList(gridContainer) {
    captureSnapshotIfNeeded();
    detachInfiniteScroll();

    var list = cleanGridContainer(gridContainer);
    if (!list) {
      gridContainer.innerHTML =
        '<div class="collection page-width"><ul class="grid product-grid grid--2-col-tablet-down grid--4-col-desktop" role="list"></ul></div>';
      list = getProductGridList(gridContainer);
    }
    list.innerHTML = '';
    guardGridList(list);
    return list;
  }

  // Renders cards RENDER_BATCH at a time; a "Show more" button under the
  // grid appends the next batch.
  function renderInBatches(list, products) {
    var shown = 0;
    var moreWrap = null;

    function appendBatch() {
      var fragment = document.createDocumentFragment();
      var end = Math.min(shown + RENDER_BATCH, products.length);
      for (var i = shown; i < end; i++) {
        fragment.appendChild(renderCard(products[i]));
      }
      list.appendChild(fragment);
      shown = end;
      if (shown >= products.length && moreWrap) {
        moreWrap.remove();
        moreWrap = null;
      }
    }

    appendBatch();

    if (shown < products.length) {
      moreWrap = document.createElement('div');
      moreWrap.className = 'brand-search-more-wrap';
      moreWrap.style.cssText = 'text-align:center;margin:2.4rem 0;';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'button brand-search-more';
      btn.textContent = getStrings().showMore;
      btn.addEventListener('click', function () {
        appendBatch();
      });
      moreWrap.appendChild(btn);
      list.parentNode.insertBefore(moreWrap, list.nextSibling);
    }
  }

  function renderResults(matches, term) {
    var gridContainer = getGridContainer();
    if (!gridContainer) return;

    var strings = getStrings();
    var list = prepareList(gridContainer);
    var status = ensureStatusEl(gridContainer);

    status.hidden = false;
    if (matches.length === 0) {
      status.textContent = formatString(strings.noResults, { terms: term });
      return;
    }
    status.textContent = formatString(strings.resultsCount, { count: matches.length });
    renderInBatches(list, matches);
  }

  function renderDateSortedResults(products) {
    var gridContainer = getGridContainer();
    if (!gridContainer) return;

    var status = ensureStatusEl(gridContainer);
    if (products.length === 0) {
      status.hidden = true;
      return;
    }

    var strings = getStrings();
    var list = prepareList(gridContainer);
    status = ensureStatusEl(gridContainer);
    status.hidden = false;
    status.textContent = formatString(strings.dateSortedStatus, { count: products.length });
    renderInBatches(list, products);
  }

  function filterToQueryTerms(products, terms) {
    var needle = (terms || '').trim().toLowerCase();
    if (!needle) return products;
    return products.filter(function (product) {
      return product.title && product.title.toLowerCase().indexOf(needle) !== -1;
    });
  }

  function sortNewestFirst(products) {
    return products.slice().sort(function (a, b) {
      return (b.createdAt || 0) - (a.createdAt || 0);
    });
  }

  function runDefaultDateSort(container) {
    showLoading();
    var terms = container.dataset.brandSearchTerms || '';
    fetchAllProducts(container).then(function (products) {
      var gridContainer = getGridContainer();
      var status = gridContainer && gridContainer.querySelector('.brand-search-status');
      if (status) status.hidden = true;
      renderDateSortedResults(sortNewestFirst(filterToQueryTerms(products, terms)));
    });
  }

  function isDateSortSelect(el) {
    return !!el && el.tagName === 'SELECT' && /^SortBy/.test(el.id);
  }

  function initDateSortOverride() {
    function handle(event) {
      var select = event.target;
      if (!isDateSortSelect(select)) return;
      var container = select.closest('.facets-container');
      if (!container || container.dataset.brandSearchScope !== 'search') return;
      if (select.value !== DATE_SORT_VALUE) return;

      event.stopPropagation();
      if (event.cancelable) event.preventDefault();

      var siblingSelects = container.querySelectorAll('select[id^="SortBy"]');
      Array.prototype.forEach.call(siblingSelects, function (other) {
        if (other !== select) other.value = DATE_SORT_VALUE;
      });

      runDefaultDateSort(container);
    }

    document.addEventListener('input', handle, true);
    document.addEventListener('change', handle, true);
  }

  function initDateSortDefault() {
    var params = new URLSearchParams(window.location.search);
    var explicitSort = params.get('sort_by');
    var shouldDefaultToDate = !explicitSort || explicitSort === DATE_SORT_VALUE;
    if (!shouldDefaultToDate) return;

    document.querySelectorAll('.facets-container[data-brand-search-scope="search"]').forEach(function (container) {
      var selects = container.querySelectorAll('select[id^="SortBy"]');
      if (!selects.length) return;
      Array.prototype.forEach.call(selects, function (select) {
        select.value = DATE_SORT_VALUE;
      });
      runDefaultDateSort(container);
    });

    if (!explicitSort) {
      try {
        params.set('sort_by', DATE_SORT_VALUE);
        var newUrl = window.location.pathname + '?' + params.toString() + window.location.hash;
        window.history.replaceState(window.history.state, '', newUrl);
      } catch (e) {}
    }
  }

  function showLoading() {
    var gridContainer = getGridContainer();
    if (!gridContainer) return;
    captureSnapshotIfNeeded();
    var status = ensureStatusEl(gridContainer);
    status.hidden = false;
    status.textContent = getStrings().loading;
  }

  function closeMobileDrawer() {
    var openDrawer = document.querySelector('.mobile-facets__disclosure[open]');
    if (openDrawer) {
      var summary = openDrawer.querySelector('summary');
      if (summary) summary.click();
    }
    // Safety net in case the drawer's own close handler didn't run.
    window.setTimeout(function () {
      var stillOpen = document.querySelector('.mobile-facets__disclosure[open]');
      if (stillOpen) {
        stillOpen.removeAttribute('open');
        stillOpen.classList.remove('menu-opening');
      }
      if (!document.querySelector('.mobile-facets__disclosure[open], .menu-drawer-container[open]')) {
        document.body.classList.remove('overflow-hidden-mobile');
      }
    }, 450);
  }

  function scrollToResults() {
    var gridContainer = getGridContainer();
    if (!gridContainer) return;
    var top = gridContainer.getBoundingClientRect().top + window.pageYOffset - 120;
    if (Math.abs(window.pageYOffset - top) > 200) {
      try {
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      } catch (e) {
        window.scrollTo(0, Math.max(top, 0));
      }
    }
  }

  var searchRunId = 0;

  function runSearch(container, term) {
    var runId = ++searchRunId;

    if (!term) {
      restoreSnapshot();
      var gridContainer = getGridContainer();
      if (gridContainer) {
        var status = gridContainer.querySelector('.brand-search-status');
        if (status) status.hidden = true;
      }
      return;
    }

    // Close the on-screen keyboard on phones so the results are visible.
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();

    // On phones the brand box lives inside Dawn's "Filter and sort" drawer.
    // That drawer locks page scrolling (body.overflow-hidden-mobile) while
    // it's open, so if it stays open after searching the results show up
    // behind it and the page can't be scrolled - which looks like the site
    // "froze after the first page". Close it and bring the results into view.
    closeMobileDrawer();

    showLoading();
    scrollToResults();

    fetchAllProducts(container).then(function (products) {
      // Ignore results of an older search if the customer searched again.
      if (runId !== searchRunId) return;
      var needle = term.trim().toLowerCase();
      var matches = products.filter(function (product) {
        return product.title && product.title.toLowerCase().indexOf(needle) !== -1;
      });
      renderResults(sortNewestFirst(matches), term);
    });
  }

  function syncInputs(value, source) {
    document.querySelectorAll('[data-brand-search-input]').forEach(function (input) {
      if (input !== source) input.value = value;
    });
  }

  // Delegated listeners bound once on the document (Dawn re-renders the
  // facets markup, so per-element listeners would go stale or stack).
  // Events from the brand box are stopped at capture so Dawn's
  // FacetFiltersForm doesn't AJAX-refresh mid-typing. Search runs on Enter
  // or the search button only.
  function initInputs() {
    if (initInputs.bound) return;
    initInputs.bound = true;

    ['input', 'change', 'keydown', 'keyup', 'keypress'].forEach(function (type) {
      document.addEventListener(
        type,
        function (event) {
          var target = event.target;
          if (!target || !target.hasAttribute) return;
          if (!target.hasAttribute('data-brand-search-input')) return;
          event.stopPropagation();
        },
        true
      );
    });

    document.addEventListener('input', function (event) {
      var input = event.target;
      if (!input || !input.hasAttribute || !input.hasAttribute('data-brand-search-input')) return;
      syncInputs(input.value, input);
      var wrapper = input.closest('.brand-search-box');
      var clearBtn = wrapper ? wrapper.querySelector('.brand-search-clear') : null;
      if (clearBtn) clearBtn.hidden = input.value.length === 0;
    });

    document.addEventListener('keydown', function (event) {
      var input = event.target;
      if (!input || !input.hasAttribute || !input.hasAttribute('data-brand-search-input')) return;
      if (event.key !== 'Enter') return;
      event.preventDefault();
      var container = input.closest('.facets-container');
      if (!container) return;
      runSearch(container, input.value.trim());
    });

    // Mobile keyboards "Go/Search" can submit the native form instead of
    // firing keydown Enter - catch that too.
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || !form.querySelector) return;
        var active = document.activeElement;
        if (!active || !active.hasAttribute || !active.hasAttribute('data-brand-search-input')) return;
        if (!form.contains(active)) return;
        event.preventDefault();
        event.stopPropagation();
        var container = active.closest('.facets-container');
        if (container) runSearch(container, active.value.trim());
      },
      true
    );

    // Start loading products in the background as soon as the customer
    // opens the mobile "Filter and sort" drawer or focuses the brand box,
    // so by the time they finish typing the data is usually already there.
    document.addEventListener(
      'focusin',
      function (event) {
        var t = event.target;
        if (!t || !t.hasAttribute || !t.hasAttribute('data-brand-search-input')) return;
        var c = t.closest('.facets-container');
        if (c) fetchAllProducts(c);
      },
      true
    );
    document.addEventListener(
      'click',
      function (event) {
        var opener = event.target.closest && event.target.closest('summary.mobile-facets__open-wrapper');
        if (!opener) return;
        var c = opener.closest('.facets-container');
        if (c && c.querySelector('[data-brand-search-input]')) fetchAllProducts(c);
      },
      true
    );

    document.addEventListener('click', function (event) {
      var clearBtn = event.target.closest && event.target.closest('.brand-search-clear');
      if (clearBtn) {
        var wrapper = clearBtn.closest('.brand-search-box');
        var input = wrapper ? wrapper.querySelector('[data-brand-search-input]') : null;
        var container = input ? input.closest('.facets-container') : null;
        document.querySelectorAll('[data-brand-search-input]').forEach(function (i) {
          i.value = '';
        });
        clearBtn.hidden = true;
        if (container) runSearch(container, '');
        return;
      }

      var submitBtn = event.target.closest && event.target.closest('.brand-search-submit');
      if (submitBtn) {
        event.preventDefault();
        var submitWrapper = submitBtn.closest('.brand-search-box');
        var submitInput = submitWrapper ? submitWrapper.querySelector('[data-brand-search-input]') : null;
        var submitContainer = submitInput ? submitInput.closest('.facets-container') : null;
        if (submitContainer) runSearch(submitContainer, submitInput.value.trim());
        return;
      }

      var toggle = event.target.closest && event.target.closest('.brand-search-toggle');
      if (toggle) {
        var box = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!box) return;
        var isHidden = box.hidden;
        box.hidden = !isHidden;
        toggle.setAttribute('aria-expanded', String(isHidden));
        if (isHidden) {
          var innerInput = box.querySelector('input');
          if (innerInput) innerInput.focus();
          // Start loading the collection's products in the background as
          // soon as the box opens, so the search feels instant on phones.
          var prefetchContainer = toggle.closest('.facets-container');
          if (prefetchContainer) fetchAllProducts(prefetchContainer);
        }
      }
    });
  }

  function init() {
    initInputs();
    initDateSortDefault();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  initDateSortOverride();

  document.addEventListener('shopify:section:load', function () {
    originalGridSnapshot = null;
    infiniteScrollOriginal = null;
    stopGuardingGrid();
    init();
  });

  var facetsWrapperObserverTargets = ['#main-collection-filters', '#main-search-filters'];
  facetsWrapperObserverTargets.forEach(function (selector) {
    var target = document.querySelector(selector);
    if (!target) return;
    var observer = new MutationObserver(
      debounce(function () {
        originalGridSnapshot = null;
        infiniteScrollOriginal = null;
        stopGuardingGrid();
        init();
      }, 50)
    );
    observer.observe(target, { childList: true, subtree: false });
  });
})();
